import { Injectable } from '@angular/core';
import firebase from 'firebase/app';
import 'firebase/functions';

export interface UploadLinkRow {
  linkId: string;
  status: 'active' | 'used' | 'revoked' | 'expired';
  createdAtMs: number;
  expiresAtMs: number;
  fileCount: number;
  bytes: number;
  /** The imaging case that receives this link's upload (already attached to the matter). */
  imagingCaseId?: string;
  sourceType?: 'hospital' | 'patient';
  /** Hospital name typed by the lawyer. */
  sourceLabel?: string | null;
  recipientEmail?: string | null;
}

export interface CreateUploadLinkRequest {
  /** Key of the matter (the `patients` document). */
  patientCaseName: string;
  hospitalName: string;
  recipientEmail: string;
  ttlDays?: number;
}

export interface UploadLinkInfo {
  expiresAtMs: number;
  maxFiles: number;
  maxBytes: number;
}

export interface SignedUpload {
  signedUrl: string;
  contentType: string;
  /** Headers the PUT must carry exactly as returned (they are part of the signature). */
  headers: Record<string, string>;
}

/**
 * Callable wrappers for the one-time patient upload links (functions repo, folder standalone-uploadLinks).
 * The staff calls need a signed-in user; the patient calls (info, sign, complete) work without a session: the token is the credential.
 */
@Injectable({ providedIn: 'root' })
export class UploadLinksService {
  private call<T>(name: string, data: unknown): Promise<T> {
    return firebase
      .functions()
      .httpsCallable(name)(data)
      .then(res => res.data as T);
  }

  // Staff (lawyer) side.
  create(request: CreateUploadLinkRequest) {
    return this.call<{ token: string; linkId: string; imagingCaseId: string; expiresAtMs: number }>('createUploadLinkV1', request);
  }

  list(patientCaseName: string) {
    return this.call<{ links: UploadLinkRow[] }>('listUploadLinksV1', { patientCaseName });
  }

  revoke(linkId: string) {
    return this.call<{ revoked: boolean }>('revokeUploadLinkV1', { linkId });
  }

  // Patient side.
  info(token: string) {
    return this.call<UploadLinkInfo>('getUploadLinkInfoV1', { token });
  }

  sign(token: string, relativePath: string, size: number, contentType: string) {
    return this.call<SignedUpload>('signUploadUrlV1', { token, relativePath, size, contentType });
  }

  complete(token: string, body: { studies: unknown[]; dicomdirRelativePath: string | null; failedFiles: string[] }) {
    return this.call<{ done: boolean; uploadId: string }>('completeUploadLinkV1', { token, ...body });
  }

  /** Address the patient opens. Built from the current origin so a link made on a test setup opens on that same setup. */
  linkUrl(token: string): string {
    return `${window.location.origin}/upload/${token}`;
  }
}
